import createFormActions from "modules/form/actions";
import { WizardActions } from "modules/wizard/actions";
import store from "services/store";
import { v4 as uuid } from "uuid";
import isEqual from "lodash/isEqual";
import historyService from "services/history";
import Validator from "services/validator";
import {
  deletePolicyModalService,
  maintenancePolicyScheduleDeleteModalService,
  maintenancePolicyScheduleModalService,
  wizardCloseModalService,
  policiesTagsCache,
  policiesActions,
  inUseTemplatesModalService,
} from "state/clusterdefinitions/services/policies";
import { generatePath } from "react-router";
import { CLUSTER_DEFINITIONS } from "utils/constants/routes";
import {
  ApplyIf,
  areValidClusterProfileTags,
  DebouncedRule,
  Missing,
  isValidName,
  KUBERNETES_NAME_REGEX,
  KUBERNETES_NAME_RULES,
} from "services/validator/rules";
import api from "services/api";
import i18next from "i18next";
import notifications from "services/notifications";
import {
  getMaintenancePolicyPayload,
  getRawPolicy,
} from "state/clusterdefinitions/selectors/policies";
import { PolicySchema } from "utils/schemas";
import { parseTagsForInput } from "utils/parsers";
import {
  DEFAULT_SCHEDULE_OPTIONS,
  MAINTENANCE_POLICY_CREATE_STEPS,
} from "utils/constants";
import { getCurrentProjectID } from "state/auth/selectors/common";
import ListActions from "modules/list/actions";
import { templatesActions } from "state/clusterdefinitions/services/templates";

const formatSchedule = (schedule = {}) => {
  const { durationHrs, name, startCron } = schedule;
  const isCronPredefined = DEFAULT_SCHEDULE_OPTIONS.find(
    (option) => option.value === startCron
  );
  return {
    name,
    startCron,
    durationHrs,
    scheduleRecurrency: isCronPredefined ? "" : startCron,
    scheduleType: isCronPredefined ? startCron : "custom",
    guid: uuid(),
  };
};

const formatMaintenancePolicy = (policy = {}) => {
  const { metadata = {}, spec = {} } = policy;
  return {
    name: metadata?.name,
    uid: metadata?.uid,
    tags: parseTagsForInput(metadata?.labels),
    schedules: (spec?.schedules || []).map(formatSchedule),
    isEdit: true,
  };
};

const validateMaintenancePolicyName = async (value) => {
  const promise = api.get(
    `v1/spcPolicies/maintenance/validate/name?name=${value}`
  );
  try {
    await promise;
  } catch (err) {
    if (err.code === "ResourceNameAlreadyExists") {
      return {
        result: i18next.t(
          `A maintenance policy with the name {{value}} already exists`,
          { value }
        ),
        field: "name",
      };
    } else {
      notifications.error({
        message: i18next.t(
          "Something went wrong while validating the maintenance policy name"
        ),
        description: err.message,
      });
    }
  }

  return false;
};

const nameValidationRule = DebouncedRule()(validateMaintenancePolicyName);

const maintenancePolicyFormValidator = new Validator();

maintenancePolicyFormValidator.addRule("name", Missing());
maintenancePolicyFormValidator.addRule(["name"], (value, key, data) => {
  const initialName =
    store.getState().forms?.maintenancePolicy?.initialData?.name || "";
  if (!value || value === initialName) {
    return;
  }
  const isValid = isValidName({
    errorMessageText: i18next.t(
      "Policy name must respect the naming guidelines"
    ),
    nameRegex: KUBERNETES_NAME_REGEX,
    rules: KUBERNETES_NAME_RULES,
    nameMaxSize: 63,
  })(value, key, data);
  if (isValid.invalidName) {
    return isValid.result;
  }
  const promise = nameValidationRule(value, key, data);
  store.dispatch({
    type: "MAINTENANCE_POLICY_NAME_VALIDATION",
    promise,
  });
  return promise;
});
maintenancePolicyFormValidator.addRule(["tags"], areValidClusterProfileTags());
maintenancePolicyFormValidator.addRule(
  ["schedules"],
  Missing({
    message: () => i18next.t("At least one schedule needs to be added"),
  })
);
export const maintenancePolicyFormActions = createFormActions({
  validator: maintenancePolicyFormValidator,
  init: async ({ isEdit }) => {
    if (isEdit) {
      const { type, id } = store.getState()?.location?.params || {};
      await store.dispatch(
        fetchPolicyDetails({
          type,
          id,
        })
      );
      const policy = getRawPolicy(store.getState()) || {};
      const formattedPolicy = formatMaintenancePolicy(policy);
      return Promise.resolve(formattedPolicy);
    }
    return Promise.resolve({
      name: "",
      tags: [],
      schedules: [],
    });
  },
  submit: async ({ isEdit = false }) => {
    const payload = getMaintenancePolicyPayload(store.getState());
    let promise;
    let successMessage;

    if (isEdit) {
      const policy = getRawPolicy(store.getState()) || {};
      const uid = policy.metadata?.uid || "";
      successMessage = i18next.t("Policy has been updated successfully");
      promise = api.put(`v1/spcPolicies/maintenance/${uid}`, payload);
    } else {
      promise = api.post("v1/spcPolicies/maintenance", payload);
      successMessage = i18next.t("Policy has been created successfully");
    }

    try {
      await promise;
      store.dispatch(clearPolicyTagsCache());
      historyService.push(
        generatePath(CLUSTER_DEFINITIONS.ROOT, { tab: "policies" })
      );
      notifications.success({
        message: successMessage,
      });
      store.dispatch({
        type: "RESET_SELECTED_POLICY",
      });
    } catch (error) {
      notifications.error({
        message: i18next.t(
          "Something went wrong while creating the maintenance policy"
        ),
        description: error.message,
      });
    }
  },
});

export const maintenancePolicyWizardActions = new WizardActions({
  formActions: maintenancePolicyFormActions,
  fieldsToValidate() {
    return {
      0: ["name", "schedules", "tags"],
    };
  },
  getDescription({ id }) {
    const formData = store.getState().forms.maintenancePolicy?.data;
    if (id === "basic-info") {
      return formData.name;
    }
  },
  steps: MAINTENANCE_POLICY_CREATE_STEPS,
});

export function initMaintenancePolicyWizard({ isEdit = false } = {}) {
  return async function thunk(dispatch) {
    dispatch(maintenancePolicyWizardActions.initialize("maintenancePolicy"));
    await dispatch(
      maintenancePolicyFormActions.init({
        module: "maintenancePolicy",
        isEdit,
      })
    );
  };
}

export function clearPolicyTagsCache() {
  return function thunk(dispatch, getState) {
    const currentProjectID = getCurrentProjectID(getState());
    dispatch(policiesTagsCache.key(currentProjectID).flushCache());
  };
}

export function closeMaintenancePolicyWizard() {
  return (_, getState) => {
    const formData = getState().forms.maintenancePolicy;

    store.dispatch({
      type: "RESET_SELECTED_POLICY",
    });

    if (isEqual(formData.data, formData.initialData)) {
      historyService.push(
        generatePath(CLUSTER_DEFINITIONS.ROOT, { tab: "policies" })
      );
      return;
    }

    wizardCloseModalService.open().then(() => {
      historyService.push(
        generatePath(CLUSTER_DEFINITIONS.ROOT, { tab: "policies" })
      );
    });
  };
}

export function openMaintenancePolicyScheduleDrawer(guid) {
  return (dispatch) => {
    maintenancePolicyScheduleModalService
      .open({ scheduleGuid: guid })
      .then(() =>
        dispatch(
          maintenancePolicyScheduleFormActions.submit({
            module: "maintenancePolicyScheduleForm",
          })
        )
      );
    dispatch(
      maintenancePolicyScheduleFormActions.init({
        module: "maintenancePolicyScheduleForm",
      })
    );
  };
}

export function onDeleteSchedule(guid) {
  return (_, getState) => {
    const schedules =
      [...getState().forms?.maintenancePolicy?.data?.schedules] || [];
    const index = schedules.findIndex((schedule) => schedule.guid === guid);
    const details = schedules[index];
    maintenancePolicyScheduleDeleteModalService
      .open({ name: details?.name })
      .then(() => {
        schedules.splice(index, 1);

        store.dispatch(
          maintenancePolicyFormActions.onChange({
            module: "maintenancePolicy",
            name: "schedules",
            value: schedules,
          })
        );
      });
  };
}

const maintenancePolicyScheduleFormValidator = new Validator();
maintenancePolicyScheduleFormValidator.addRule(
  ["name", "scheduleType"],
  Missing()
);
maintenancePolicyScheduleFormValidator.addRule(["name"], (value, key, data) => {
  const isValid = isValidName({
    errorMessageText: i18next.t(
      "Schedule name must respect the naming guidelines"
    ),
    nameRegex: KUBERNETES_NAME_REGEX,
    rules: KUBERNETES_NAME_RULES,
    nameMaxSize: 63,
  })(value, key, data);
  if (isValid.invalidName) {
    return isValid.result;
  }
});
maintenancePolicyScheduleFormValidator.addRule(
  "scheduleRecurrency",
  ApplyIf(
    (_v, _k, data) => data.scheduleType === "custom",
    Missing({
      message: () => i18next.t("Please select at least one option"),
    })
  )
);
maintenancePolicyScheduleFormValidator.addRule("name", (value, _k) => {
  const initialName =
    store.getState().forms.maintenancePolicyScheduleForm?.initialData?.name;
  const allNames = (
    store.getState().forms?.maintenancePolicy?.data?.schedules || []
  )
    .map((schedule) => schedule.name)
    .filter((name) => name !== initialName);
  if (allNames.includes(value)) {
    return {
      field: "name",
      result: i18next.t("Schedule name already exists"),
    };
  }
});
maintenancePolicyScheduleFormValidator.addRule("durationHrs", Missing());
export const maintenancePolicyScheduleFormActions = createFormActions({
  validator: maintenancePolicyScheduleFormValidator,
  init: async () => {
    const selectedScheduleGuid =
      maintenancePolicyScheduleModalService.data?.scheduleGuid;
    const schedules =
      store.getState().forms?.maintenancePolicy?.data?.schedules || [];
    if (selectedScheduleGuid) {
      const scheduleDetails = schedules.find(
        (schedule) => schedule.guid === selectedScheduleGuid
      );

      return Promise.resolve(scheduleDetails);
    }
    return Promise.resolve({
      name: "",
      scheduleType: "0 0 * * SUN",
      scheduleRecurrency: "",
      durationHrs: 1,
    });
  },
  submit: ({
    name = "",
    scheduleRecurrency = "",
    scheduleType = "",
    durationHrs = 1,
    guid = "",
  }) => {
    const schedules =
      [...store.getState().forms?.maintenancePolicy?.data?.schedules] || [];

    const newSchedule = {
      name,
      scheduleRecurrency,
      scheduleType,
      durationHrs,
      startCron: scheduleType !== "custom" ? scheduleType : scheduleRecurrency,
      guid: guid || uuid(),
    };

    if (guid) {
      const index = schedules.findIndex((schedule) => schedule.guid === guid);
      schedules.splice(index, 1, newSchedule);
    } else {
      schedules.push(newSchedule);
    }

    store.dispatch(
      maintenancePolicyFormActions.onChange({
        module: "maintenancePolicy",
        name: "schedules",
        value: schedules,
      })
    );
  },
});

export function deletePolicy({ uid, name }) {
  return (dispatch, getState) => {
    deletePolicyModalService.open({ uid, name }).then(async () => {
      try {
        await api.delete(`v1/spcPolicies/${uid}`);
        const currentProjectId = getCurrentProjectID(getState());
        dispatch(clearPolicyTagsCache());
        notifications.success({
          message: "Policy has been deleted successfully",
        });
        dispatch(policiesActions.refreshItems(`policies/${currentProjectId}`));
        dispatch(
          policiesActions.fetchListingFilters(`policies/${currentProjectId}`)
        );
      } catch (error) {
        notifications.error({
          message: i18next.t(
            "Something went wrong while trying to delete the policy"
          ),
          description: error.message,
        });
      }
    });
  };
}

export function onEditPolicy(data) {
  return () => {
    const uid = data?.metadata?.uid || "";
    const type = data?.spec?.kind || "";

    historyService.push(
      generatePath(CLUSTER_DEFINITIONS.EDIT_POLICY, {
        type,
        id: uid,
      })
    );
  };
}

export function showInUseTemplates({ policyUID }) {
  return async (dispatch, getState) => {
    const currentProjectId = getCurrentProjectID(getState());
    await dispatch(
      inUseTemplatesActions.initialize(`inUseTemplates/${currentProjectId}`, {
        limit: 25,
        policyUID,
      })
    );
    inUseTemplatesModalService.open();
  };
}

export function fetchPolicyDetails({ type, id }) {
  return async (dispatch) => {
    const promise = api.get(`v1/spcPolicies/${type}/${id}`);
    dispatch({
      promise,
      type: "FETCH_POLICY_DETAILS",
      schema: PolicySchema,
    });
    try {
      await promise;
    } catch (error) {
      notifications.error({
        message: i18next.t(
          "Something went wrong while trying to fetch the policy details"
        ),
        description: error?.message,
      });
    }
  };
}

export const inUseTemplatesActions = new ListActions({
  ...templatesActions,
  filtersConfig: {
    ...templatesActions.filtersConfig,
    showExtendedFilters: false,
  },
});
